import { z } from 'zod';
import { CustomerOptionSchema } from './customer.js';

/**
 * Roles per ADR-0005. `customer-operator` is reserved for v2 (table is
 * shipped in v1, role string is enabled later via feature flag).
 */
export const UserRoleEnum = z.enum(['admin', 'operator', 'customer-operator']);
export type UserRole = z.infer<typeof UserRoleEnum>;

export const UserStatusEnum = z.enum(['active', 'disabled']);
export type UserStatus = z.infer<typeof UserStatusEnum>;

export const UserLanguageEnum = z.enum(['de', 'en']);
export type UserLanguage = z.infer<typeof UserLanguageEnum>;

export const UserSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(200).nullable().optional(),
  email: z.string().email(),
  role: UserRoleEnum,
  status: UserStatusEnum,
  defaultCustomerId: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  lastLoginAt: z.string().datetime().nullable().optional(),
});
export type User = z.infer<typeof UserSchema>;

export const AdminUserListItemSchema = UserSchema.extend({
  customers: z.array(CustomerOptionSchema),
});
export type AdminUserListItem = z.infer<typeof AdminUserListItemSchema>;

export const AdminUserListResponseSchema = z.object({
  items: z.array(AdminUserListItemSchema),
});
export type AdminUserListResponse = z.infer<typeof AdminUserListResponseSchema>;

export const CreateAdminUserRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  email: z.string().email(),
  password: z.string().min(12).max(200),
  role: UserRoleEnum,
  status: UserStatusEnum.default('active'),
  customerIds: z.array(z.string().min(1)).min(1),
});
export type CreateAdminUserRequest = z.infer<typeof CreateAdminUserRequestSchema>;

export const UpdateAdminUserRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).nullable().optional(),
  email: z.string().email(),
  password: z.string().min(12).max(200).optional(),
  customerIds: z.array(z.string().min(1)),
});
export type UpdateAdminUserRequest = z.infer<typeof UpdateAdminUserRequestSchema>;

export const DeleteAdminUserParamsSchema = z.object({
  id: z.string().min(1),
});
export type DeleteAdminUserParams = z.infer<typeof DeleteAdminUserParamsSchema>;

export const UpdateCurrentUserSettingsRequestSchema = z.object({
  language: UserLanguageEnum,
  defaultCustomerId: z.string().min(1).nullable().optional(),
});
export type UpdateCurrentUserSettingsRequest = z.infer<typeof UpdateCurrentUserSettingsRequestSchema>;
